import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UpBoardAdminUsersComponent } from '../up-board-admin-users/up-board-admin-users.component';
import { UpBoardAdminStyleComponent } from '../up-board-admin-style/up-board-admin-style.component';
import { UpBoardAdminLogoComponent } from '../up-board-admin-logo/up-board-admin-logo.component';
import { UpBoardAdminConfigComponent } from '../up-board-admin-config/up-board-admin-config.component';
import { TutorialService } from 'src/app/shared/services/tutorial.service';
import { TutorialModalService } from 'src/app/shared/services/tutorial-modal.service';
import { TutorialStateService } from 'src/app/shared/services/tutorial-state.service';
import { BoardPermissionsService } from 'src/app/shared/services/board-permissions.service';

@Component({
  selector: 'app-up-board-admin',
  templateUrl: './up-board-admin.component.html',
  styleUrls: ['./up-board-admin.component.css']
})
export class UpBoardAdminComponent implements OnInit, OnDestroy {

  public url: string = '';
  public usersSelected: boolean = false;
  public styleSelected: boolean = false;
  public logoSelected: boolean = false;
  public configSelected: boolean = false;
  public activeTab: string = 'users';

  private boardId: string | null = null;
  private userEmail: string | null = null;
  private tutorialShown: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public location: Location,
    private auth: AngularFireAuth,
    private db: AngularFirestore,
    private tutorialService: TutorialService,
    private tutorialModalService: TutorialModalService,
    private tutorialStateService: TutorialStateService,
    private boardPermissionsService: BoardPermissionsService
  ) {
  }

  onActivate($event: any) {
    this.usersSelected = $event instanceof UpBoardAdminUsersComponent;
    this.styleSelected = $event instanceof UpBoardAdminStyleComponent;
    this.logoSelected = $event instanceof UpBoardAdminLogoComponent;
    this.configSelected = $event instanceof UpBoardAdminConfigComponent;

    // Note: Tutorial triggering is now handled in ngOnInit when board config loads
  }

  /**
   * Set the active tab for visual feedback
   * @param tabName The name of the tab to activate
   */
  setActiveTab(tabName: string): void {
    this.activeTab = tabName;
  }

   onAttach($event: any) {
     // Child component attached - no action needed
   }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
      // Get boardId from parent route params (admin is a child route of /boards/:id/admin)
      this.boardId = this.route.snapshot.paramMap.get('id');
      console.log('[Tutorial] Admin component init - boardId:', this.boardId);
      
      // Get current user email
      const sub = this.auth.user.subscribe((u) => {
        sub.unsubscribe();
        if (u?.email) {
          this.userEmail = u.email;
          console.log('[Tutorial] Admin component - user email:', this.userEmail);
          
          // Initialize services if boardId is available
          if (this.boardId) {
            console.log('[Tutorial] Admin component - initializing tutorial services');
            this.boardPermissionsService.init(this.boardId, this.userEmail);
            this.tutorialStateService.init(this.boardId, this.userEmail);
            
            // Subscribe to board config and trigger tutorial when it loads
            // This ensures the board document and feature gates are available
            this.db
              .collection('boards')
              .doc<any>(this.boardId)
              .valueChanges()
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (boardDoc) => {
                  // Trigger tutorials when board config loads (after feature gates are known)
                  this.triggerAdminTutorial(boardDoc, this.userEmail!);
                },
                error: (error) => {
                  console.error('[Tutorial] Error fetching board for admin tutorial:', error);
                }
              });
          }
        }
      });
    }

    /**
     * Show tutorials when admin pages are first activated.
     * Called when board config loads to ensure feature gates are initialized.
     * 
     * Only shows tutorial once per admin session to avoid showing tutorials repeatedly
     * when board config changes (e.g., when toggling feature flags).
     * 
     * Waits for tutorial state to be loaded from Firestore before filtering tutorials.
     * This prevents the race condition where tutorials are shown even if they've been
     * completed/dismissed on another device.
     * 
     * @param boardDoc The board document with config
     * @param userEmail The current user's email
     */
    private async triggerAdminTutorial(boardDoc: any, userEmail: string): Promise<void> {
      if (!this.boardId) {
        return;
      }

      // Only show tutorial once per admin session
      if (this.tutorialShown) {
        return;
      }

      try {
        // Wait for tutorial state to be loaded
        await this.tutorialStateService.waitForInitialization();

        if (!boardDoc) {
          console.log('[Tutorial] Board document not found');
          this.tutorialShown = true;
          return;
        }

        const availableTutorials = this.tutorialService.getTutorialsForBoard(boardDoc);
        
        console.log(`[Tutorial] Available tutorials for admin:`, availableTutorials.map(t => t.id));

        // Filter out completed/dismissed tutorials
        const incompleteTutorials = this.tutorialService.filterCompletedTutorials(availableTutorials);
        console.log(`[Tutorial] Incomplete tutorials for admin:`, incompleteTutorials.map(t => t.id));

        if (incompleteTutorials.length > 0) {
          const firstTutorial = incompleteTutorials[0];
          const userIsAdmin = this.boardPermissionsService.userHasAdminPermissions(this.boardPermissionsService.boardUser) || false;
          
          console.log(`[Tutorial] Showing ${firstTutorial.id} tutorial on admin page`);
          this.tutorialModalService.showTutorial(firstTutorial, this.boardId!, userEmail, userIsAdmin);
          this.tutorialShown = true;
        } else {
          console.log(`[Tutorial] All tutorials already completed for admin`);
          this.tutorialShown = true;
        }
      } catch (error) {
        console.error('[Tutorial] Error triggering admin tutorial:', error);
        this.tutorialShown = true;
      }
    }
}
